import React, { useState, useEffect, useRef } from 'react';
import clsx from 'clsx';
import styles from './DesktopView.module.css';

interface ProgressiveMainImageProps {
  src: string;
  thumbnailUrl?: string;
  alt: string;
  isZoomEnabled: boolean;
  onClick: (e: React.MouseEvent<HTMLImageElement>) => void;
}

/**
 * Three-layer image renderer for the fullscreen modal:
 *  1. Blurred+darkened thumbnail — acts as the sizing anchor for the wrapper so the
 *     skeleton has actual dimensions while the hi-res image is still downloading.
 *  2. Shimmer skeleton — animates on top of the blurred thumbnail.
 *  3. Full-resolution image — positioned absolutely, fades in once `onLoad` fires.
 *
 * Resets when `src` changes so switching slides never leaves the previous picture
 * hanging while the new one downloads.
 *
 * Fallback: no thumbnail → render the main image as the sizing anchor, skeleton
 * overlays on top (legacy behaviour, used for edge cases without a thumb).
 */
export const ProgressiveMainImage: React.FC<ProgressiveMainImageProps> = ({
  src,
  thumbnailUrl,
  alt,
  isZoomEnabled,
  onClick,
}) => {
  const [loaded, setLoaded] = useState(false);
  const lastSrcRef = useRef<string>(src);

  useEffect(() => {
    if (lastSrcRef.current !== src) {
      lastSrcRef.current = src;
      setLoaded(false);
    }
  }, [src]);

  const hasThumbnail = !!thumbnailUrl && thumbnailUrl !== src;

  if (hasThumbnail) {
    return (
      <>
        <img
          src={thumbnailUrl}
          alt=""
          className={clsx(styles.thumbnailBackdrop, loaded && styles.thumbnailBackdropHidden)}
          aria-hidden="true"
          draggable={false}
          crossOrigin="anonymous"
        />
        {!loaded && <div className={styles.mainImageSkeleton} aria-hidden="true" />}
        <img
          key={src}
          src={src}
          alt={alt}
          className={clsx(styles.mainImage, styles.mainImageAbsolute, loaded && styles.mainImageLoaded)}
          onLoad={() => setLoaded(true)}
          onClick={onClick}
          style={{ cursor: isZoomEnabled ? 'zoom-out' : 'zoom-in' }}
          crossOrigin="anonymous"
        />
      </>
    );
  }

  // No thumbnail — main image sizes itself, skeleton overlays briefly on top.
  return (
    <>
      {!loaded && <div className={styles.mainImageSkeleton} aria-hidden="true" />}
      <img
        key={src}
        src={src}
        alt={alt}
        className={clsx(styles.mainImage, loaded && styles.mainImageLoaded)}
        onLoad={() => setLoaded(true)}
        onClick={onClick}
        style={{ cursor: isZoomEnabled ? 'zoom-out' : 'zoom-in' }}
        crossOrigin="anonymous"
      />
    </>
  );
};

export default ProgressiveMainImage;
